/**
 * Decodificador de BR Code (PIX copia e cola).
 *
 * O payload segue o padrão EMV MPM: campos TLV `ID(2) + LEN(2) + VALUE(LEN)`,
 * onde LEN é contado em BYTES UTF-8 — por isso todo o parsing acontece sobre
 * Uint8Array, e não sobre String.length, que quebraria em nomes acentuados.
 */

const PIX_GUI = "BR.GOV.BCB.PIX";
const MERCHANT_ACCOUNT_FIRST_ID = 26;
const MERCHANT_ACCOUNT_LAST_ID = 51;
const MIN_PAYLOAD_BYTES = 20;
const MAX_PAYLOAD_BYTES = 1024;

const encoder = new TextEncoder();
const decoder = new TextDecoder();

/** CRC16-CCITT (polinômio 0x1021, valor inicial 0xFFFF), como exige o campo 63. */
function crc16(bytes) {
  let crc = 0xffff;

  for (let i = 0; i < bytes.length; i++) {
    crc ^= bytes[i] << 8;
    for (let bit = 0; bit < 8; bit++) {
      crc = crc & 0x8000 ? ((crc << 1) ^ 0x1021) & 0xffff : (crc << 1) & 0xffff;
    }
  }

  return crc.toString(16).toUpperCase().padStart(4, "0");
}

/** Retorna a lista de campos TLV, ou null quando a estrutura está corrompida. */
function parseTlv(bytes) {
  const fields = [];
  let cursor = 0;

  while (cursor < bytes.length) {
    if (cursor + 4 > bytes.length) return null;

    const id = decoder.decode(bytes.slice(cursor, cursor + 2));
    const rawLength = decoder.decode(bytes.slice(cursor + 2, cursor + 4));
    if (!/^\d{2}$/.test(rawLength)) return null;

    const length = parseInt(rawLength, 10);
    if (cursor + 4 + length > bytes.length) return null;

    const valueBytes = bytes.slice(cursor + 4, cursor + 4 + length);
    fields.push({ id, value: decoder.decode(valueBytes), valueBytes });
    cursor += 4 + length;
  }

  return fields;
}

function readField(fields, id) {
  const field = fields.find((item) => item.id === id);
  return field ? field.value : "";
}

function readSubFields(fields, id) {
  const field = fields.find((item) => item.id === id);
  if (!field) return null;
  return parseTlv(field.valueBytes);
}

/** Localiza o campo 26-51 cujo subcampo 00 é a GUI do arranjo PIX. */
function findPixMerchantAccount(fields) {
  for (const field of fields) {
    const id = Number(field.id);
    if (!Number.isInteger(id)) continue;
    if (id < MERCHANT_ACCOUNT_FIRST_ID || id > MERCHANT_ACCOUNT_LAST_ID)
      continue;

    const subFields = parseTlv(field.valueBytes);
    if (!subFields) continue;
    if (readField(subFields, "00").toUpperCase() === PIX_GUI) return subFields;
  }

  return null;
}

function invalid(error) {
  return {
    valid: false,
    error,
    payload: "",
    isDynamic: false,
    pixKey: "",
    merchantName: "",
    merchantCity: "",
    amount: null,
    txid: "",
  };
}

export function parseEmvPix(raw) {
  const payload = String(raw || "")
    .replace(/[\r\n\t]/g, "")
    .trim();
  const bytes = encoder.encode(payload);

  if (bytes.length < MIN_PAYLOAD_BYTES || bytes.length > MAX_PAYLOAD_BYTES) {
    return invalid("Código PIX com tamanho inválido.");
  }

  if (!payload.startsWith("000201")) {
    return invalid("Código PIX inválido: identificador inicial ausente.");
  }

  // O campo 63 (CRC) é sempre o último e é ASCII, então recortar pelo fim é seguro.
  if (payload.slice(-8, -4) !== "6304") {
    return invalid("Código PIX inválido: dígito verificador ausente.");
  }

  const expectedCrc = payload.slice(-4).toUpperCase();
  if (crc16(encoder.encode(payload.slice(0, -4))) !== expectedCrc) {
    return invalid(
      "Código PIX inválido ou incompleto: dígito verificador não confere.",
    );
  }

  const fields = parseTlv(bytes);
  if (!fields) {
    return invalid("Código PIX inválido: estrutura do código está corrompida.");
  }

  const merchantAccount = findPixMerchantAccount(fields);
  if (!merchantAccount) {
    return invalid("Código inválido: não é um QR Code PIX.");
  }

  const additionalData = readSubFields(fields, "62");
  const rawAmount = readField(fields, "54");
  const amount = rawAmount === "" ? NaN : Number(rawAmount);

  return {
    valid: true,
    error: null,
    payload,
    isDynamic: readField(merchantAccount, "25") !== "",
    pixKey: readField(merchantAccount, "01"),
    merchantName: readField(fields, "59").trim(),
    merchantCity: readField(fields, "60").trim(),
    amount: Number.isFinite(amount) && amount > 0 ? amount : null,
    txid: additionalData ? readField(additionalData, "05") : "",
  };
}
